import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import FileDialog from "../components/dialog.tsx";
import Loader from "../components/loader.tsx";
import { CreateCodebookRequest } from "@ns/api";
import { toaster } from "../components/ui/toaster.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { PlateTypeEnum } from "@ns/types";
import {
  UpdateWarehouse,
  updateWarehouseSchema,
} from "../schemas/update-warehouses.schema.ts";
import FileUpload from "../components/form/file-upload.tsx";
import { useUpload } from "../utils/hooks/use-upload.ts";

const SingleWarehouseForm = () => {
  const { setValue, reset, handleSubmit } = useFormContext<UpdateWarehouse>();
  const { upload, isProgress } = useUpload();

  const [codebookFiles, setCodebookFiles] = useState<string[]>([]);

  const { id } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ["codebook", id],
    queryFn: () => api.codebook.getCodebookById(id!),
    enabled: !!id,
    staleTime: 0,
    cacheTime: 0,
  });

  const handleUploadCodebook = async (file: File) => {
    const formData: FormData = new FormData();
    formData.append("file", file);

    upload(formData)
      .then((response: string) => {
        setCodebookFiles((prevState) => [...prevState, response]);
      })
      .catch((error: unknown) => {
        console.log((error as Error).message);
      });
  };

  useEffect(() => {
    setValue("width", data?.codebook?.width ?? "");
    setValue("height", data?.codebook?.height ?? "");
    setValue("thickness", data?.codebook?.thickness ?? "");
    setValue("price", data?.codebook?.price?.toString() ?? "");
    setValue("location", data?.codebook?.location ?? "");
    setValue("quantity", data?.codebook?.quantity?.toString() ?? "");
  }, [data, setValue]);

  const resetFields = () => {
    reset({
      price: "",
    });
  };

  const { data: codebookWarehouse } = useQuery({
    queryKey: ["codebookWarehouse", data?.codebook?.codebookWarehouseId ?? ""],
    queryFn: () =>
      api.codebookWarehouse.getCodebookWarehouseById(
        data!.codebook!.codebookWarehouseId!,
      ),
    enabled: !!data?.codebook?.codebookWarehouseId,
  });

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: CreateCodebookRequest) =>
      api.codebook.updateCodebook(id!, request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi nalog!",
        description: "Novi nalog je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      resetFields();
      setCodebookFiles([]);
      queryClient.invalidateQueries({ queryKey: ["codebook"] });
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({
      codebookWarehouseId: codebookWarehouse!.id!,
      width: data.width,
      height: data.height,
      thickness: data.thickness,
      price: Number(data.price),
      quantity: Number(data.quantity),
      location: data.location,
      codebookFiles,
    });
  });

  if (isLoading || isProgress) {
    return <Loader />;
  }

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className={"text-[24px] text-black font-semibold"}>LAGER</span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={false}
              onClick={onSubmit}
            >
              <IoCheckmark /> Izmeni
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <div
            className={
              "flex flex-row gap-[1rem] md:gap-[1rem] lg:gap-[1.25rem] justify-between px-[0.25rem]"
            }
          >
            <ControlledInput
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              name={"width"}
              label={"Širina (m1)"}
              placeholder={"(m1)"}
            />
            <ControlledInput
              name={"height"}
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              label={"Dužina (m1)"}
              placeholder={"(m1)"}
            />
            <ControlledInput
              name={"thickness"}
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              label={"Debljina (m1)"}
              placeholder={"(m1)"}
            />
          </div>
          {codebookWarehouse?.plateType &&
            codebookWarehouse.plateType === PlateTypeEnum.COMMERCIAL_STONE && (
              <ControlledInput
                className="w-full sm:w-[24rem] md:w-[28rem]"
                name={"price"}
                placeholder={"Unesi cenu materijala (EUR/m2)"}
                label={"Cena materijala (EUR/m2)"}
              />
            )}
          {codebookWarehouse?.plateType &&
            codebookWarehouse.plateType === PlateTypeEnum.TABLE && (
              <ControlledInput
                className="w-full sm:w-[24rem] md:w-[28rem]"
                name={"price"}
                placeholder={"Unesi cenu materijala (EUR/tabla)"}
                label={"Cena materijala (EUR/tabla)"}
              />
            )}
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"quantity"}
            placeholder={"Kolicinu"}
            label={"Kolicina"}
            disabled={false}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"location"}
            placeholder={"Unesi lokaciju"}
            label={"Lokacija"}
            disabled={false}
          />
          <FileDialog
            label={"Pregled fotografija"}
            title={"Fotografije materijala"}
            files={data!.codebookFiles}
          />
          <FileUpload
            maxFiles={10}
            label={"Nove fotografije"}
            onUpload={handleUploadCodebook}
            files={[]}
          />
          <div className={"h-[1rem]"}></div>
        </div>
      </div>
    </div>
  );
};

export const SingeWarehousePage = () => {
  return (
    <ControlledForm schema={updateWarehouseSchema}>
      <SingleWarehouseForm />
    </ControlledForm>
  );
};
